import React, { SelectHTMLAttributes, useState, ReactNode, useEffect, useRef } from 'react';
import CurrencyFormat from 'react-currency-format';
import { CaretDownIcon } from '../Icons';
import cn from '@/utils/cn';
import { useClickOutside } from '@/hooks/useClickOutside';

const ButtonClassName = cn([
  'bg-emerge-base-3',
  'border',
  'border-emerge-navy-4',
  'text-emerge-navy-2',
  'text-xs',
  'font-medium',
  'rounded-lg',
  'block',
  'px-4',
  'py-3',
  'flex',
  'flex-row',
  'justify-between',
  'items-center',
  'gap-x-2',
  'hover:bg-emerge-base-2',
  'active:bg-emerge-base-2',
  'focus:outline-none',
  'transition-colors',
  'cursor-pointer'
]);

const SelectOptionsClassName = cn([
  'z-10',
  'absolute',
  'top-full',
  'left-0',
  'mt-0',
  'bg-white',
  'rounded-b-lg',
  'border',
  'border-t-0',
  'border-emerge-teal',
  'shadow-[0px_2px_12px_rgba(0,0,0,0.05),_8px_20px_21px_rgba(0,0,0,0.04),_18px_45px_29px_rgba(0,0,0,0.03),_32px_80px_34px_rgba(0,0,0,0.01)]',
  'w-full',
  'h-auto',
  'overflow-y-auto',
  'max-h-96',
  'vertical-scroll'
]);

export type TSelectOptions = {
  label: string;
  labelDescription?: string;
  value: string | undefined;
  color?: string;
};

interface SelectInputProps extends SelectHTMLAttributes<HTMLLIElement> {
  options: Array<TSelectOptions>;
  onOptionSelected?: (option: TSelectOptions, optionIndex: number) => void;
  header?: ReactNode;
  optionsContainerClass?: string;
}

const SelectInput: React.FC<SelectInputProps> = ({
  options,
  onOptionSelected: optionSelectedHandler,
  header,
  optionsContainerClass,
  className,
  value: valueFromParent
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const filterRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(filterRef, () => {
    setShowOptions(false);
  });

  const hasLabelDescription = (option: TSelectOptions) => option.label && option.labelDescription;
  const isNumeric = (label: string) => !isNaN(+label);
  const buttonValue = valueFromParent ?? options[selectedIndex]?.label ?? '';

  const onOptionSelected = (option: TSelectOptions, optionIndex: number) => {
    if (optionSelectedHandler) {
      optionSelectedHandler(option, optionIndex);
    }

    setSelectedIndex(optionIndex);
    setShowOptions(false);
  };

  const handleOptionChange = (label: typeof valueFromParent) => {
    const newOptionIndex = options.findIndex((option) => option.label === label);
    setSelectedIndex(newOptionIndex);
  };

  useEffect(() => {
    if (valueFromParent) {
      handleOptionChange(valueFromParent);
    }
  }, [buttonValue]);

  return (
    <div ref={filterRef} className="flex justify-end relative w-full">
      <button
        onClick={() => {
          setShowOptions((prev) => !prev);
        }}
        className={cn(
          ButtonClassName,
          className,
          showOptions ? 'border-emerge-teal rounded-b-none rounded-t-lg' : 'border-emerge-navy-4'
        )}
        type="button"
        value={buttonValue}>
        <span className="text-xs flex items-center overflow-x-hidden whitespace-nowrap text-left w-full">
          {buttonValue}
        </span>
        <span>
          <CaretDownIcon
            size="xs"
            className={cn('text-emerge-navy stroke-2', showOptions ? 'rotate-180' : '')}
          />
        </span>
      </button>

      <div
        className={cn(SelectOptionsClassName, optionsContainerClass, !showOptions ? 'hidden' : '')}>
        {header}
        <ul className="text-xs text-dark-navy-30 cursor-pointer">
          {options.map((option, optionIndex) => {
            return (
              <li
                onClick={() =>
                  optionIndex !== selectedIndex ? onOptionSelected(option, optionIndex) : null
                }
                key={optionIndex}
                value={option.value}>
                {hasLabelDescription(option) ? (
                  <a
                    className={`block px-4 py-2${optionIndex === selectedIndex ? ' cursor-default bg-emerge-base-2' : ' hover:bg-emerge-base-2'} transition-colors text-sm`}
                    style={{ wordBreak: 'break-all' }}>
                    <span
                      className={`flex flex-col font-medium${optionIndex === selectedIndex ? ' font-semibold' : ''}`}>
                      {option.label}
                      {isNumeric(option.labelDescription!) ? (
                        <CurrencyFormat
                          className="text-xs"
                          value={Number(option.labelDescription).toFixed(2)}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'$'}
                          decimalScale={2}
                        />
                      ) : (
                        option.labelDescription
                      )}
                    </span>
                  </a>
                ) : (
                  <a
                    className={`block px-4 flex items-center py-2${optionIndex === selectedIndex ? ' cursor-default bg-emerge-base-2' : ' hover:bg-emerge-base-2'} transition-colors`}
                    style={{ wordBreak: 'break-all' }}>
                    <span
                      className={`font-medium${optionIndex === selectedIndex ? ' font-semibold' : ''}`}>
                      {option.label}
                    </span>
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default SelectInput;
